import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { rpc, useRpcEvent } from '../api/rpc'
import type { Approval, AppNotification, PendingPromptInfo, PromptResponse } from '../api/types'
import { PromptCard } from '../components/chat/PromptCard'
import { IconCheck, IconChat, IconClock, IconInbox, IconShield, IconWarning } from '../components/icons'
import { Button } from '../components/ui/Button'
import { Badge, EmptyState, type BadgeTone } from '../components/ui/Card'
import { FilterChips } from '../components/ui/DataTable'
import { useToast } from '../components/ui/Toast'
import { cn } from '../lib/cn'
import { formatDateTime } from '../lib/format'

function toPrompt(a: Approval): PendingPromptInfo {
  return {
    promptId: a.id, kind: a.kind, question: a.payload.question, options: a.payload.options,
    multiple: a.payload.multiple, custom: a.payload.custom,
    submitLabel: a.payload.submitLabel, skipLabel: a.payload.skipLabel, customLabel: a.payload.customLabel,
    call: a.payload.call ? { ...a.payload.call, permission: 'pending' } : undefined
  }
}

function sourceLink(a: Approval): string | null {
  if (a.chatId) return `/chat/${a.chatId}`
  if (a.runId) return `/automations/runs/${a.runId}`
  return null
}

const pad = (n: number) => String(n).padStart(2, '0')

/** `m:ss` under an hour, `h:mm:ss` above it — the countdown that makes the deadline feel real. */
function countdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`
}

/** Row-sized timestamp: the clock for today, the date for anything older. */
function shortTime(ts: number, locale: string): string {
  const sameDay = new Date(ts).toDateString() === new Date().toDateString()
  return new Intl.DateTimeFormat(locale, sameDay
    ? { hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit' }).format(ts)
}

/** One second ticks only while something can actually expire. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [active])
  return now
}

/** The human part of a tool call: its first non-empty string argument. */
function previewInput(input: Record<string, unknown> | undefined): string | null {
  if (!input) return null
  const text = Object.values(input).find(v => typeof v === 'string' && v.trim())
  return typeof text === 'string' ? text : JSON.stringify(input)
}

type ListFilter = 'all' | 'approvals' | 'notes' | 'handled'
type Entry = { key: string; approval: Approval } | { key: string; note: AppNotification }

function statusTone(a: Approval): BadgeTone {
  if (a.status === 'pending') return 'warning'
  if (a.status === 'answered') return a.response?.allow ? 'success' : 'neutral'
  return 'neutral'
}

/** Notification icon + tint: a failure must be visible from across the room, a success is quiet. */
function noteTone(n: AppNotification): { cls: string; icon: React.ReactNode } {
  if (n.type === 'run-failed' || n.type === 'connector-error' || n.type === 'budget') {
    return { cls: 'bg-red-50 text-red-600', icon: <IconWarning width={16} height={16} /> }
  }
  if (n.type === 'run-succeeded') return { cls: 'bg-mint-50 text-mint-500', icon: <IconCheck width={16} height={16} /> }
  if (n.type === 'approval-needed') return { cls: 'bg-amber-50 text-amber-700', icon: <IconShield width={16} height={16} /> }
  return { cls: 'bg-cream-200 text-ink-400', icon: <IconInbox width={16} height={16} /> }
}

/** A list row: a real button where the detail pane exists, plain text (or a link) on phones. */
function ListRow({ selected, onSelect, href, children }: {
  selected?: boolean; onSelect?(): void; href?: string; children: React.ReactNode
}) {
  const cls = cn(
    'flex w-full items-start gap-3 rounded-[10px] p-3 text-left transition-colors',
    selected ? 'bg-cream-200' : 'hover:bg-cream-100'
  )
  return (
    <>
      <button type="button" aria-pressed={selected} onClick={onSelect} className={cn(cls, 'max-lg:hidden')}>{children}</button>
      {href
        ? <Link to={href} className={cn(cls, 'lg:hidden')}>{children}</Link>
        : <div className={cn(cls, 'lg:hidden')}>{children}</div>}
    </>
  )
}

export function InboxPage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [pending, setPending] = useState<Approval[]>([])
  const [history, setHistory] = useState<Approval[]>([])
  const [notes, setNotes] = useState<AppNotification[]>([])
  const [filter, setFilter] = useState<ListFilter>('all')
  const [picked, setPicked] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    const [p, all, n] = await Promise.all([
      rpc.call<{ items: Approval[] }>('approvals.list', { status: 'pending' }),
      rpc.call<{ items: Approval[] }>('approvals.list', { limit: 30 }),
      rpc.call<{ items: AppNotification[] }>('notifications.list', { limit: 50 })
    ])
    setPending(p.items)
    setHistory(all.items.filter(a => a.status !== 'pending'))
    setNotes(n.items)
  }, [])
  useEffect(() => { void load().catch(() => undefined) }, [load])
  useRpcEvent('approval.created', () => void load())
  useRpcEvent('approval.resolved', () => void load())
  useRpcEvent('notification', () => void load())

  const now = useNow(pending.length > 0)

  const respond = async (a: Approval, r: PromptResponse) => {
    setBusyId(a.id)
    try { await rpc.call('approvals.respond', { approvalId: a.id, response: r }) }
    catch (err) { toast((err as Error).message, 'error') }
    finally { setBusyId(null) }
  }

  const entries = useMemo<Entry[]>(() => {
    if (filter === 'handled') return history.map(a => ({ key: `a:${a.id}`, approval: a }))
    const out: Entry[] = []
    if (filter !== 'notes') out.push(...pending.map(a => ({ key: `a:${a.id}`, approval: a })))
    if (filter !== 'approvals') out.push(...notes.map(n => ({ key: `n:${n.id}`, note: n })))
    return out
  }, [filter, history, pending, notes])

  const selected = entries.find(e => e.key === picked) ?? entries[0]
  const unread = notes.filter(n => !n.readAt).length

  const tabs: { value: ListFilter; label: string; count?: number }[] = [
    { value: 'all', label: t('inbox.all') },
    { value: 'approvals', label: t('inbox.tabApprovals'), count: pending.length },
    { value: 'notes', label: t('inbox.tabNotifications'), count: notes.length },
    ...(history.length > 0 ? [{ value: 'handled' as const, label: t('inbox.tabHandled'), count: history.length }] : [])
  ]

  const rowTime = (ts: number) => (
    <span className="shrink-0 font-mono text-xs text-ink-500 tabular-nums">{shortTime(ts, i18n.language)}</span>
  )

  const approvalRow = (a: Approval) => {
    const title = a.kind === 'question' ? (a.payload.question ?? t('inbox.open')) : t('chat.prompt.permission', { tool: a.payload.call?.tool ?? '' })
    const isPending = a.status === 'pending'
    return (
      <li key={a.id}>
        <ListRow selected={selected?.key === `a:${a.id}`} onSelect={() => setPicked(`a:${a.id}`)}>
          <span className={cn('flex h-8 w-8 flex-none items-center justify-center rounded-lg',
            isPending ? 'bg-amber-50 text-amber-700' : 'bg-cream-200 text-ink-400')}>
            {a.kind === 'question' ? <IconChat width={16} height={16} /> : <IconShield width={16} height={16} />}
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-baseline gap-2">
              <span className={cn('min-w-0 flex-1 truncate text-sm', isPending ? 'font-semibold text-ink-100' : 'font-medium text-ink-300')}>{title}</span>
              {isPending
                ? <span className="shrink-0 font-mono text-xs text-amber-700 tabular-nums">{countdown(a.expiresAt - now)}</span>
                : rowTime(a.createdAt)}
            </span>
            <span className="truncate text-[12.5px] text-ink-400">
              {a.payload.context ?? t(`inbox.source_${a.source}`)}
              <span className="text-ink-500"> · {t(`inbox.source_${a.source}`)}</span>
            </span>
            {a.payload.call && <span className="truncate font-mono text-xs text-ink-500">{a.payload.call.tool}</span>}
          </span>
        </ListRow>
        {isPending && (
          <div className="px-3 pb-3 lg:hidden">
            <PromptCard prompt={toPrompt(a)} onRespond={r => respond(a, r)} />
          </div>
        )}
      </li>
    )
  }

  const noteRow = (n: AppNotification) => {
    const tone = noteTone(n)
    return (
      <li key={n.id}>
        <ListRow selected={selected?.key === `n:${n.id}`} onSelect={() => setPicked(`n:${n.id}`)} href={n.link || undefined}>
          <span className={cn('flex h-8 w-8 flex-none items-center justify-center rounded-lg', tone.cls)}>{tone.icon}</span>
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-baseline gap-2">
              <span className={cn('min-w-0 flex-1 truncate text-sm', n.readAt ? 'font-normal text-ink-300' : 'font-semibold text-ink-100')}>{n.title}</span>
              {!n.readAt && <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-ginger-500" aria-label={t('inbox.unread')} />}
              {rowTime(n.createdAt)}
            </span>
            {n.body && <span className="truncate text-[12.5px] text-ink-400">{n.body}</span>}
          </span>
        </ListRow>
      </li>
    )
  }

  const handledRow = (a: Approval) => (
    <li key={a.id}>
      <ListRow selected={selected?.key === `a:${a.id}`} onSelect={() => setPicked(`a:${a.id}`)}>
        <span className={cn('flex h-8 w-8 flex-none items-center justify-center rounded-lg',
          a.response?.allow ? 'bg-mint-50 text-mint-500' : 'bg-cream-200 text-ink-400')}>
          {a.status === 'expired' || a.status === 'cancelled' ? <IconClock width={16} height={16} /> : <IconCheck width={16} height={16} />}
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-300">{a.payload.call?.tool ?? a.payload.question ?? t('inbox.open')}</span>
            {rowTime(a.createdAt)}
          </span>
          <span className="truncate text-[12.5px] text-ink-400">
            {t(`inbox.status_${a.status}`)}
            {a.payload.context && <span className="text-ink-500"> · {a.payload.context}</span>}
          </span>
        </span>
      </ListRow>
    </li>
  )

  const detail = () => {
    if (!selected) {
      return (
        <div className="mx-auto w-full max-w-[720px] px-6 pt-10">
          <EmptyState title={t('inbox.emptyDetail')} icon={<IconInbox />} />
        </div>
      )
    }

    if ('note' in selected) {
      const n = selected.note
      const tone = noteTone(n)
      return (
        <div className="mx-auto flex min-h-full w-full max-w-[720px] flex-col gap-6 px-6 pb-10 pt-8 max-sm:px-4">
          <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-300">
            <span className={cn('flex h-8 w-8 items-center justify-center rounded-lg', tone.cls)}>{tone.icon}</span>
            <span>{formatDateTime(n.createdAt, i18n.language)}</span>
            {!n.readAt && <Badge tone="accent">{t('inbox.unread')}</Badge>}
          </div>
          <h2 className="text-[22px] font-semibold leading-[30px] tracking-tight text-ink-100">{n.title}</h2>
          {n.body && <p className="whitespace-pre-wrap text-sm leading-6 text-ink-200">{n.body}</p>}
          {n.link && (
            <div>
              <Link
                to={n.link}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-cream-300 bg-cream-50 px-3.5 text-[13.5px] font-medium text-ink-100 transition-colors hover:bg-cream-200"
              >
                {t('inbox.open')}
              </Link>
            </div>
          )}
        </div>
      )
    }

    const a = selected.approval
    const isPending = a.status === 'pending'
    const link = sourceLink(a)
    const total = a.expiresAt - a.createdAt
    const left = Math.max(0, a.expiresAt - now)
    const pct = total > 0 ? Math.min(100, Math.max(0, (left / total) * 100)) : 0
    const title = a.kind === 'question' ? (a.payload.question ?? t('inbox.open')) : t('chat.prompt.permission', { tool: a.payload.call?.tool ?? '' })
    const preview = previewInput(a.payload.call?.input)

    const rows: { label: string; value: React.ReactNode }[] = []
    if (a.payload.call) {
      rows.push({
        label: t('inbox.fieldTool'),
        value: <span className="rounded-[5px] bg-cream-200 px-1.5 py-0.5 font-mono text-[13px] text-ink-300">{a.payload.call.tool}</span>
      })
    }
    rows.push({
      label: t('inbox.fieldSource'),
      value: (
        <span className="flex flex-wrap items-center gap-1.5">
          <span>{a.payload.context ?? t(`inbox.source_${a.source}`)}</span>
          <span className="text-ink-500">· {t(`inbox.source_${a.source}`)}</span>
          {link && <Link to={link} className="font-medium text-ginger-600 hover:underline">{t('inbox.open')}</Link>}
        </span>
      )
    })
    rows.push({ label: t('inbox.fieldCreated'), value: formatDateTime(a.createdAt, i18n.language) })
    if (isPending) rows.push({ label: t('inbox.fieldExpires'), value: formatDateTime(a.expiresAt, i18n.language) })
    if (a.response?.text) rows.push({ label: t('inbox.fieldAnswer'), value: a.response.text })

    return (
      <div className="mx-auto flex min-h-full w-full max-w-[720px] flex-col px-6 pb-8 pt-8 max-sm:px-4">
        <div className="flex flex-1 flex-col gap-6">
          <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-300">
            <Badge tone={statusTone(a)}>
              <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
              {t(`inbox.status_${a.status}`)}
            </Badge>
            <span>{t('inbox.fromSource', { source: t(`inbox.source_${a.source}`) })}</span>
            {link
              ? <Link to={link} className="font-medium text-ink-100 hover:underline">{a.payload.context ?? t('inbox.open')}</Link>
              : a.payload.context && <span className="font-medium text-ink-100">{a.payload.context}</span>}
            <span className="text-ink-500">· {formatDateTime(a.createdAt, i18n.language)}</span>
          </div>

          <div className="flex flex-col gap-1.5">
            <h2 className="text-[22px] font-semibold leading-[30px] tracking-tight text-ink-100">{title}</h2>
            <p className="text-sm leading-6 text-ink-300">
              {a.kind === 'question' ? t('inbox.questionLede') : t('inbox.permissionLede')}
            </p>
          </div>

          {isPending && (
            <div className="flex items-center gap-3 rounded-[10px] bg-amber-50 px-4 py-3 text-amber-700">
              <IconClock width={18} height={18} />
              <span className="flex-1 text-sm font-medium">
                {t('inbox.autoDenyIn')} <span className="font-mono tabular-nums">{countdown(left)}</span>
              </span>
              <span className="h-1 w-[120px] shrink-0 overflow-hidden rounded-full bg-cream-50 max-sm:hidden">
                <span className="block h-full rounded-full bg-amber-400" style={{ width: `${pct}%` }} />
              </span>
            </div>
          )}

          <div className="overflow-hidden rounded-xl border border-line bg-cream-50">
            {rows.map((r, i) => (
              <div
                key={r.label}
                className={cn('grid grid-cols-[140px_minmax(0,1fr)] gap-4 px-4 py-3 max-sm:grid-cols-1 max-sm:gap-1', i > 0 && 'border-t border-line')}
              >
                <span className="text-[13px] text-ink-500">{r.label}</span>
                <span className="min-w-0 text-sm text-ink-100">{r.value}</span>
              </div>
            ))}
          </div>

          {a.kind === 'question' && isPending && (
            <PromptCard prompt={toPrompt(a)} onRespond={r => respond(a, r)} />
          )}

          {a.payload.call && preview && (
            <div className="flex flex-col gap-2">
              <span className="text-[13px] font-semibold text-ink-100">{t('inbox.arguments')}</span>
              <div className="whitespace-pre-wrap rounded-[10px] bg-cream-100 px-4 py-3.5 text-sm leading-[22px] text-ink-200">{preview}</div>
            </div>
          )}

          {a.payload.call && (
            <details className="rounded-[10px] border border-line">
              <summary className="cursor-pointer px-3.5 py-2.5 text-[13px] font-medium text-ink-300">{t('inbox.argsJson')}</summary>
              <pre className="overflow-x-auto whitespace-pre-wrap border-t border-line px-3.5 py-3 font-mono text-[12.5px] leading-5 text-ink-300">
                {JSON.stringify(a.payload.call.input, null, 2)}
              </pre>
            </details>
          )}
        </div>

        {isPending && a.kind === 'permission' && (
          <div className="sticky bottom-0 mt-6 flex flex-wrap items-center gap-2 border-t border-line bg-canvas pt-4">
            <Button disabled={busyId === a.id} onClick={() => void respond(a, { allow: true })}>{t('chat.prompt.allowOnce')}</Button>
            <Button variant="secondary" disabled={busyId === a.id} onClick={() => void respond(a, { allow: true, always: true })}>{t('chat.prompt.allowAlways')}</Button>
            <span className="flex-1" />
            <Button variant="danger" disabled={busyId === a.id} onClick={() => void respond(a, { allow: false })}>{t('chat.prompt.deny')}</Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0">
      <section
        aria-label={t('inbox.title')}
        className="flex w-[380px] max-w-[420px] shrink-0 flex-col border-r border-line max-lg:w-full max-lg:max-w-none max-lg:border-r-0"
      >
        <div className="flex flex-col gap-3 px-5 pt-6">
          <div className="flex items-center gap-2">
            <h1 className="flex-1 text-[20px] font-semibold leading-7 tracking-tight text-ink-100">{t('inbox.title')}</h1>
            <Button
              variant="ghost" size="sm" disabled={unread === 0}
              onClick={async () => { await rpc.call('notifications.markRead', {}); void load() }}
            >
              {t('inbox.markAllRead')}
            </Button>
          </div>
          <FilterChips<ListFilter> label={t('inbox.filterLabel')} value={filter} onChange={setFilter} options={tabs} />
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-4 pt-2">
          {entries.length === 0 ? (
            <div className="px-3 pt-4">
              <EmptyState title={t('inbox.emptyList')} icon={<IconInbox />} />
            </div>
          ) : (
            <>
              {(filter === 'all' || filter === 'approvals') && pending.length > 0 && (
                <section>
                  <h2 className="px-3 pb-1.5 pt-2.5 text-xs font-medium text-ink-500">{t('inbox.pending')}</h2>
                  <ul className="flex flex-col gap-0.5">{pending.map(approvalRow)}</ul>
                </section>
              )}
              {(filter === 'all' || filter === 'notes') && notes.length > 0 && (
                <section>
                  <h2 className="px-3 pb-1.5 pt-2.5 text-xs font-medium text-ink-500">{t('inbox.notifications')}</h2>
                  <ul className="flex flex-col gap-0.5">{notes.map(noteRow)}</ul>
                </section>
              )}
              {filter === 'handled' && (
                <section>
                  <h2 className="px-3 pb-1.5 pt-2.5 text-xs font-medium text-ink-500">{t('inbox.history')}</h2>
                  <ul className="flex flex-col gap-0.5">{history.map(handledRow)}</ul>
                </section>
              )}
            </>
          )}
        </div>
      </section>

      <section aria-label={t('inbox.detail')} className="min-w-0 flex-1 overflow-y-auto max-lg:hidden">
        {detail()}
      </section>
    </div>
  )
}
